import { Title } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Box, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import axios from "axios";
import { useState } from "react";
import { SettingsItem } from "./SettingsItem";
import { ChartingRecallSetting } from "./ChartingRecallSetting";
import { FacilitySetting } from "../services/workplaceSettings";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { useUpdateIndependentContractorBadge } from "@src/appV2/FacilityProfile/api/useUpdateIndependentContractorBadge";
import { IndependentContractorBadgeGeneratedBy } from "@src/appV2/IndependentContractorBadge/analytics";
import { SessionType } from "@src/modules/interface";
import { useSelector } from "react-redux";
import { CHARTING_INCOMPATIBLE_WORKPLACE_TYPES } from "@src/appV2/Charting/constants";
import { useUpdateSignOutWithQrCode } from "@src/appV2/FacilityProfile/api/useUpdateSignOutWithQrCode";
import { FACILITY_ROLES } from "@src/constants/facility";

interface DocumentationPreferencesProps {
  workplace: WorkplaceProfileResponse;
  onPreferencesChanged: () => void;
}

export function DocumentationPreferences(props: DocumentationPreferencesProps) {
  const { workplace, onPreferencesChanged } = props;
  const [selectedIndependentContractorBadgeToggle, setSelectedIndependentContractorBadgeToggle] =
    useState<boolean>(workplace?.data.attributes.independentContractorBadge?.enabled ?? false);

  const { showSuccessToast } = useToast();
  const { user, adminProfile, admin: isAdmin } = useSelector((state: SessionType) => state.session);
  const workplaceId = workplace.data.id;
  const signOutWithQrCode = workplace.data.attributes.signOutWithQrCode ?? false;

  const {
    isLoading: isUpdatingIndependentContractorBadge,
    mutateAsync: updateIndependentContractorBadgeMutation,
  } = useUpdateIndependentContractorBadge();

  // Only show if the setting exists in the workplace attributes
  const canUpdateIndependentContractorBadgeEnabled =
    isAdmin && isDefined(workplace?.data.attributes.independentContractorBadge);
  const canUpdateSignOutWithQrCode =
    isAdmin ||
    user?.access?.includes(FACILITY_ROLES.ADMIN) ||
    user?.access?.includes(FACILITY_ROLES.SHIFT_MANAGEMENT);

  const updateIndependentContractorBadgeEnabled = async (enabled: boolean) => {
    const userMeta = isAdmin
      ? {
          userId: adminProfile?.userId,
          userName: adminProfile?.name,
          generatedBy: IndependentContractorBadgeGeneratedBy.ADMIN,
        }
      : {
          userId: user?._id,
          userName: user?.name,
          generatedBy: IndependentContractorBadgeGeneratedBy.WORKPLACE,
        };

    logEvent(HCF_USER_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_TOGGLED, {
      workplaceId,
      workplaceName: workplace.data.attributes.name,
      ...userMeta,
      value: enabled,
    });

    const previousValue = selectedIndependentContractorBadgeToggle;
    setSelectedIndependentContractorBadgeToggle(enabled);

    try {
      await updateIndependentContractorBadgeMutation({ workplaceId, enabled });

      showSuccessToast("Independent contractor badge preferences updated successfully");
      onPreferencesChanged();
    } catch (error: unknown) {
      logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SETTING_UPDATE_FAILURE, {
        workplaceId,
        ...userMeta,
        value: enabled,
      });

      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast("Error updating preferences. Please, try again later or contact support");
      }

      // Fallback to previous value if the update fails
      setSelectedIndependentContractorBadgeToggle(previousValue);
    }
  };

  const { mutateAsync: updateSignOutWithQrCode, isLoading: isUpdatingSignOutWithQrCode } =
    useUpdateSignOutWithQrCode({
      meta: {
        userSuccessMessage: "QR code sign out was updated successfully.",
        userErrorMessage: "Failed to update QR code sign out.",
      },
    });

  const settingsItems: FacilitySetting[] = [
    {
      key: "independentContractorBadgeEnabled",
      title: "Require a badge during shifts",
      subtitle:
        "This workplace requires that all Independent Contractors using Clipboard wear a badge at all times during a shift.",
      checked: selectedIndependentContractorBadgeToggle,
      onChange: (checked: boolean) => {
        void updateIndependentContractorBadgeEnabled(checked);
      },
      loading: isUpdatingIndependentContractorBadge,
      hidden: !canUpdateIndependentContractorBadgeEnabled,
    },
    {
      key: "signOutWithQrCode",
      title: "Enable QR code sign out",
      subtitle:
        "When enabled, staff at your workplace can scan a QR code to provide signatures and anonymous feedback at the end of shifts.",
      checked: signOutWithQrCode,
      onChange: async (value: boolean) => {
        await updateSignOutWithQrCode({
          workplaceId,
          signOutWithQrCode: value,
        });
        onPreferencesChanged();
      },
      loading: isUpdatingSignOutWithQrCode,
      hidden: !canUpdateSignOutWithQrCode,
    },
  ];

  const isChartingRecallVisible = !CHARTING_INCOMPATIBLE_WORKPLACE_TYPES.includes(
    workplace.data.attributes.type ?? ""
  );
  const isAtLeastOneSettingVisible =
    settingsItems.some((setting) => !setting.hidden) || isChartingRecallVisible;
  const isLoading = settingsItems.some((setting) => setting.loading);

  return isAtLeastOneSettingVisible ? (
    <Stack position="relative" pl={2} sx={{ opacity: isLoading ? 0.5 : 1 }}>
      <Title component="h3">Documentation Preferences</Title>

      {isLoading ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}

      {settingsItems.map((setting) => {
        return (
          <SettingsItem key={setting.key} item={setting} isLoading={setting.loading ?? false} />
        );
      })}
      {isChartingRecallVisible && (
        <ChartingRecallSetting
          workplace={workplace}
          onPreferencesChanged={onPreferencesChanged}
          isAdmin={isAdmin}
        />
      )}
    </Stack>
  ) : null;
}
